import React, { useState } from 'react';
import { toast } from 'react-toastify';

// Duplication d'une pièce (2 étapes : destination + nom, puis équipements à copier
// et leur nombre) ou d'une section (nom seul). Les usages ne sont jamais copiés
// (cf. DB_processor.duplicate_room / duplicate_section) : ils sont à saisir ou
// répartir sur la copie.
export default function DuplicateZoneModal({ kind, item, sections, equipments, translate, onClose, onDone }) {
    const isRoom = kind === 'room';
    const roomEquipments = isRoom
        ? equipments.filter(equipment => equipment.location?.room_id === item._id)
        : [];
    const currentSection = isRoom ? sections.find(section => section._id === item.id_section) : item;
    const otherSections = sections.filter(section => section._id !== currentSection?._id);
    const originalName = isRoom ? item.name_room : item.name_section;

    const [step, setStep] = useState(1);
    const [destination, setDestination] = useState('same'); // 'same' | 'other'
    const [targetSectionId, setTargetSectionId] = useState('');
    const [name, setName] = useState(`${originalName} (copie)`);
    const [nameTouched, setNameTouched] = useState(false);
    const [counts, setCounts] = useState(() => Object.fromEntries(
        roomEquipments.map(equipment => [equipment._id, Number(equipment.technical_data?.nb_equipment) || 1])
    ));
    const [included, setIncluded] = useState(() => new Set(roomEquipments.map(equipment => equipment._id)));
    const [busy, setBusy] = useState(false);

    const nbIdentical = Number(item.nb_identical_rooms) || 1;
    const hasEquipmentStep = isRoom && roomEquipments.length > 0;
    const destinationReady = destination === 'same' || !!targetSectionId;

    // Dans une autre section, la copie garde par défaut le nom d'origine
    // (« Sanitaires » de l'étage 2), dans la même section on suffixe « (copie) ».
    const handleDestinationChange = (value) => {
        setDestination(value);
        if (!nameTouched) setName(value === 'same' ? `${originalName} (copie)` : originalName);
    };

    const toggleIncluded = (equipmentId) => {
        setIncluded(prev => {
            const next = new Set(prev);
            if (next.has(equipmentId)) next.delete(equipmentId);
            else next.add(equipmentId);
            return next;
        });
    };

    const changeCount = (equipmentId, delta) => {
        setCounts(prev => ({ ...prev, [equipmentId]: Math.max(1, (prev[equipmentId] || 1) + delta) }));
    };

    const postJson = async (url, method, body) => {
        const response = await fetch(`${process.env.REACT_APP_API_URL}${url}`, {
            method,
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(body),
        });
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        return response.json();
    };

    const handleIncrementIdentical = async () => {
        setBusy(true);
        try {
            await postJson(`/update_room/${item._id}`, 'PUT', { nb_identical_rooms: nbIdentical + 1 });
            toast.success(`« ${originalName} » compte maintenant ${nbIdentical + 1} pièces identiques.`);
            onDone({ sectionId: item.id_section, roomId: item._id });
        } catch {
            toast.error('Erreur lors de la mise à jour de la pièce.');
            setBusy(false);
        }
    };

    const handleDuplicate = async () => {
        if (!name.trim()) return;
        setBusy(true);
        try {
            if (isRoom) {
                const targetId = destination === 'same' ? item.id_section : targetSectionId;
                const data = await postJson('/duplicate_room', 'POST', {
                    room_id: item._id,
                    new_room_name: name.trim(),
                    target_section_id: targetId,
                    equipments: roomEquipments
                        .filter(equipment => included.has(equipment._id))
                        .map(equipment => ({ equipment_id: equipment._id, nb_equipment: counts[equipment._id] })),
                });
                toast.success('Pièce dupliquée. Pensez à saisir ou répartir ses usages.');
                onDone({ sectionId: targetId, roomId: data.room._id });
            } else {
                const data = await postJson('/duplicate_section', 'POST', {
                    section_id: item._id,
                    new_section_name: name.trim(),
                });
                toast.success('Section dupliquée. Pensez à saisir ou répartir ses usages.');
                onDone({ sectionId: data.section._id, roomId: data.rooms[0]?._id ?? null });
            }
        } catch {
            toast.error('Erreur lors de la duplication.');
            setBusy(false);
        }
    };

    const equipmentLabel = (equipment) => {
        const detail = equipment.name_equipment || equipment.technical_data?.reference;
        const water = equipment.water_value ? `${equipment.water_value} ${equipment.water_value_unit || ''}`.trim() : null;
        return [translate(equipment.type_equipment), detail, water].filter(Boolean).join(' · ');
    };

    const renderRoomStep1 = () => (
        <>
            <div className="dup-hint">
                <span>
                    Pièce strictement identique ? Augmentez plutôt le nombre de pièces identiques :
                    les volumes sont multipliés sans créer de copie.
                </span>
                <button type="button" className="dup-hint-btn" onClick={handleIncrementIdentical} disabled={busy}>
                    +1 pièce identique (×{nbIdentical} → ×{nbIdentical + 1})
                </button>
            </div>

            <span className="dup-label">Où dupliquer ?</span>
            <div className="dup-choices">
                <label className={`dup-choice${destination === 'same' ? ' dup-choice--active' : ''}`}>
                    <input type="radio" name="dup-destination" checked={destination === 'same'}
                        onChange={() => handleDestinationChange('same')} />
                    <span>Dans cette section<small>{currentSection?.name_section}</small></span>
                </label>
                <label className={`dup-choice${destination === 'other' ? ' dup-choice--active' : ''}${otherSections.length === 0 ? ' dup-choice--disabled' : ''}`}>
                    <input type="radio" name="dup-destination" checked={destination === 'other'}
                        disabled={otherSections.length === 0}
                        onChange={() => handleDestinationChange('other')} />
                    <span>Dans une autre section<small>{otherSections.length === 0 ? 'Aucune autre section' : 'À choisir ci-dessous'}</small></span>
                </label>
            </div>
            {destination === 'other' && (
                <select value={targetSectionId} onChange={(e) => setTargetSectionId(e.target.value)}>
                    <option value="">Choisir la section de destination</option>
                    {otherSections.map(section => (
                        <option key={section._id} value={section._id}>{section.name_section}</option>
                    ))}
                </select>
            )}
        </>
    );

    const renderRoomStep2 = () => (
        <>
            <span className="dup-label">Équipements à copier</span>
            <div className="dup-eq-list">
                {roomEquipments.map(equipment => {
                    const isIncluded = included.has(equipment._id);
                    return (
                        <div key={equipment._id} className={`dup-eq-row${isIncluded ? '' : ' dup-eq-row--excluded'}`}>
                            <label className="dup-eq-name">
                                <input type="checkbox" checked={isIncluded} onChange={() => toggleIncluded(equipment._id)} />
                                <span>{equipmentLabel(equipment)}</span>
                            </label>
                            <div className="dup-stepper">
                                <button type="button" disabled={!isIncluded || counts[equipment._id] <= 1}
                                    onClick={() => changeCount(equipment._id, -1)} aria-label="Retirer un équipement">−</button>
                                <span>{counts[equipment._id]}</span>
                                <button type="button" disabled={!isIncluded}
                                    onClick={() => changeCount(equipment._id, 1)} aria-label="Ajouter un équipement">+</button>
                            </div>
                        </div>
                    );
                })}
            </div>
        </>
    );

    const submitLabel = busy
        ? 'Duplication…'
        : hasEquipmentStep
            ? `Dupliquer avec ${included.size} équipement${included.size > 1 ? 's' : ''}`
            : 'Dupliquer';

    return (
        <div className="section-popup">
            <div className="section-popup-content">
                <span className="close" onClick={onClose}>&times;</span>
                <h2>{isRoom ? 'Dupliquer la pièce' : 'Dupliquer la section'}</h2>
                {hasEquipmentStep && <p className="dup-step">Étape {step} sur 2</p>}
                <p className="dup-subtitle">
                    {isRoom
                        ? `« ${originalName} » et ses équipements seront copiés, sans les usages (à saisir ou répartir ensuite).`
                        : `« ${originalName} », ses pièces et leurs équipements seront copiés, sans les usages (à saisir ou répartir ensuite).`}
                </p>

                <div className="dup-body">
                    {isRoom && step === 1 && renderRoomStep1()}
                    {isRoom && step === 2 && renderRoomStep2()}

                    {step === 1 && (
                        <>
                            <label className="dup-label" htmlFor="dup-name">
                                {isRoom ? 'Nom de la nouvelle pièce' : 'Nom de la nouvelle section'}
                            </label>
                            <input id="dup-name" type="text" value={name}
                                onChange={(e) => { setName(e.target.value); setNameTouched(true); }} />
                        </>
                    )}
                </div>

                <div className="dup-footer">
                    {step === 2 && (
                        <button type="button" className="dup-back-btn" onClick={() => setStep(1)} disabled={busy}>← Retour</button>
                    )}
                    {step === 1 && hasEquipmentStep ? (
                        <button type="button" className="add-equipment-button"
                            disabled={!destinationReady || !name.trim()}
                            onClick={() => setStep(2)}>
                            Suivant →
                        </button>
                    ) : (
                        <button type="button" className="add-equipment-button"
                            disabled={busy || !destinationReady || !name.trim()}
                            onClick={handleDuplicate}>
                            {submitLabel}
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
}
